import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button, Text } from 'src/@vymo/ui/atoms';
import { Alert } from 'src/@vymo/ui/blocks';
import { Form } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import classnames from 'classnames';
import { ReactComponent as CopilotIcon } from 'src/assets/icons/copilot_icon.svg';
import { ReactComponent as ExternalLink } from 'src/assets/icons/externalLink.svg';
import { ReactComponent as RetryIcon } from 'src/assets/icons/refresh.svg';
import { Keys, locale } from 'src/i18n';
import { getCurrentUser } from 'src/models/lmsConfig/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import RecordingBanner from './features/recordingBanner';
import RenderTable from './messageType/render.table';
import { getFileViewer, performCtaAction } from './queries';
import {
  getIsRecording,
  getIsTyping,
  getLastUpdated,
  getMessages,
  getReplies,
  getSampleQuestions,
  getStatus,
} from './selector';
import { connectWebSocket, sendMessage } from './thunk';
import { CopilotVersion } from './types';
import { sendMessage as sendWorkflowMessage } from './workflow/thunk';
import styles from './index.module.scss';

function Body({
  version,
  chatSessionId,
}: {
  version?: CopilotVersion;
  chatSessionId: string;
}) {
  const messages = useAppSelector(getMessages);
  const dispatch = useAppDispatch();
  const formRef = useRef(null);
  const followUpQuestions = useAppSelector(getReplies);
  const sampleQuestions = useAppSelector(getSampleQuestions);
  const status = useAppSelector(getStatus);
  const isTyping = useAppSelector(getIsTyping);
  const showRecordingBanner = useAppSelector(getIsRecording);
  const lastUpdated = useAppSelector(getLastUpdated);

  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  const user = getCurrentUser();

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: lastUpdated ? 'smooth' : 'auto',
        block: 'start',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, followUpQuestions]);

  useEffect(() => {
    if (status?.message) setIsBannerVisible(true);
    else setIsBannerVisible(false);
  }, [status?.message]);

  const getTextClasses = useCallback(
    (sender: string) =>
      classnames(styles.copilotv2__body__message__text, {
        [styles.copilotv2__body__message__text__ai]: sender === 'AI',
        [styles.copilotv2__body__message__text__user]: sender === 'USER',
      }),
    [],
  );

  const handleFormSubmission = () => {
    (async () => {
      // @ts-ignore
      const result = await formRef.current?.getFieldsForSubmission?.();
      if (result?.valid === 'valid') {
        if (version === 'workflow') {
          dispatch(
            sendWorkflowMessage({
              text: (result?.data || [])
                ?.map((inp) => `${inp.name} is ${inp.value}`)
                ?.join('. '),
              chatSessionId,
            }),
          );
        } else {
          dispatch(
            sendMessage({
              text: (result.data || [])
                ?.map((inp) => `${inp.hint}: ${inp.value}`)
                ?.join('\n'),
              metaData: { value: result.data },
            }),
          );
        }
      }
    })();
  };

  return (
    <div className={styles.copilotv2__body}>
      {isBannerVisible && (
        <Alert
          key={status?.message}
          onClose={() => setIsBannerVisible(false)}
          variant={status?.type}
          data-test-id="copilot-toast"
          duration={
            status?.type !== 'error' || status?.duration
              ? status?.duration || 2000
              : null
          }
          banner
          closeable={Boolean(status?.type !== 'error' || status?.duration)}
        >
          <div className={styles.copilotv2__body__status}>
            {status?.message}{' '}
            {Boolean(status?.type === 'error' && !status?.duration) && (
              <Button
                onClick={() => dispatch(connectWebSocket())}
                type="text"
                rounded
                isIcon
                size="small"
                iconProps={{
                  icon: <RetryIcon />,
                  iconPosition: 'left',
                }}
              />
            )}
          </div>
        </Alert>
      )}
      <div className={styles.copilotv2__body__main}>
        <span>
          <Text type="h4">
            👋
            <br />
            Hello {user.name},
            <br />
            How can I help you today?
          </Text>
        </span>
        {sampleQuestions.map((sampleQuestion) => (
          <Button
            type="outlined"
            variant="info"
            className={styles.copilotv2__body__button}
            onClick={() => dispatch(sendMessage({ text: sampleQuestion }))}
          >
            {sampleQuestion}
          </Button>
        ))}
        {messages.map((message, idx) => (
          <div className={styles.copilotv2__body__messageWrapper}>
            <div
              ref={idx === messages.length - 1 ? lastMessageRef : null}
              className={getTextClasses(message.sender)}
            >
              <Text type="label">
                <>
                  {!_.isEmpty(message.file_data) &&
                    getFileViewer(message.file_data)}
                  <ReactMarkdown>{message.text}</ReactMarkdown>
                </>
              </Text>
            </div>
            {Boolean(message?.tabular_data?.length) && (
              <RenderTable data={message.tabular_data} />
            )}
            {(message?.ctas || []).map((cta) => (
              <Button
                type={
                  cta.type === 'lead' || cta.type === 'list'
                    ? 'text'
                    : 'outlined'
                }
                variant="info"
                className={styles.copilotv2__body__button}
                onClick={() => performCtaAction(cta, dispatch, chatSessionId)}
                iconProps={
                  cta.type === 'lead' || cta.type === 'list'
                    ? {
                        icon: <ExternalLink />,
                        iconPosition: 'right',
                      }
                    : null
                }
              >
                {cta.title}
              </Button>
            ))}
            {idx === messages.length - 1 &&
              message.metadata?.requiredInputs?.length > 0 && (
                <div className={styles.copilotv2__body__form}>
                  <Form
                    // @ts-ignore
                    ref={formRef}
                    id="copilotForm"
                    name="copilotForm"
                    onChange={() => {}}
                    span="1fr"
                    config={{
                      version: FormVersion.webRevyre,
                      // @ts-ignore
                      data: message.metadata.requiredInputs,
                      grouping: [],
                      fieldItemConfig: {
                        showDisabledIcon: true,
                      },
                      beforeSaveHookConfig: {
                        multimedia: {
                          isMultimediaBeforeSaveHookDisabled: false,
                          valueFormat: 'url',
                        },
                      },
                    }}
                  />
                  <Button
                    type="primary"
                    className={styles.copilotv2__body__button}
                    onClick={() => handleFormSubmission()}
                  >
                    Submit
                  </Button>
                </div>
              )}
          </div>
        ))}
        {Boolean(followUpQuestions.length) && (
          <>
            <Text type="sublabel" bold>
              {locale(Keys.RELATED_QUESTIONS)}
            </Text>
            {followUpQuestions.map((followUpQuestion) => (
              <Button
                type="outlined"
                variant="subtle"
                className={styles.copilotv2__body__button}
                onClick={() =>
                  dispatch(sendMessage({ text: followUpQuestion }))
                }
              >
                {followUpQuestion}
              </Button>
            ))}
          </>
        )}

        {isTyping && (
          <span className={styles.copilotv2__body__thinking}>
            <CopilotIcon />
            <Text
              type="label"
              classNames={styles.copilotv2__body__thinkingText}
            >
              {' '}
              Thinking
            </Text>
          </span>
        )}
      </div>

      {showRecordingBanner && <RecordingBanner />}
    </div>
  );
}

export default Body;
